import React from 'react'
import { Link } from 'react-router-dom'
import i18n from '../../../../i18n'
import { DataIndex, LegalFormalityIndexTableType } from './types'
import { ColumnType } from 'antd/es/table'
import styled from 'styled-components'

export const purchaseStatusesList = () => {
  const purchaseStatuses = i18n.t('purchases:status', {
    returnObjects: true,
  })
  return Object.entries(purchaseStatuses).map(([key, name]) => {
    return {
      text: name,
      value: key,
    }
  })
}

export const getPurchaseStatusColumnSearchProps = (
  variables: any,
  dataIndex: DataIndex
): ColumnType<LegalFormalityIndexTableType> => ({
  filtered: !!variables.filters?.[`${dataIndex}`],
  filteredValue: variables.filters?.[`${dataIndex}`],
  filters: purchaseStatusesList(),
})

export const formalitiesStatusesList = () => {
  return i18n.t('legalFormalities:status.name', {
    returnObjects: true,
  })
}

export const formalitiesStatusesOptionsList = () => {
  return Object.entries(formalitiesStatusesList()).map(([key, name]) => {
    return {
      text: name,
      value: key,
    }
  })
}

export const defaultFormalityStatuses = () => formalitiesStatusesOptionsList()

export const formalitiesStatusDefaultValues = () =>
  defaultFormalityStatuses().map((option: any) => {
    return option.value
  })

export const getFormalitiesStatusColumnSearchProps = (
  variables: any,
  dataIndex: string
): ColumnType<LegalFormalityIndexTableType> => ({
  defaultFilteredValue: formalitiesStatusDefaultValues(),
  filterResetToDefaultFilteredValue: true,
  filtered: !!variables.filters?.[`${dataIndex}`],
  filteredValue: variables.filters?.[`${dataIndex}`],
  filters: formalitiesStatusesOptionsList(),
})

export const assignedUserEmailsList = (legalisteData: any) => {
  if (!legalisteData || !legalisteData.legalists) {
    return []
  }
  return legalisteData.legalists.map((legalist: any) => ({
    text: legalist.email,
    value: legalist.email,
  }))
}

export const getAssignedUserColumnSearchProps = (
  variables: any,
  dataIndex: string,
  legalisteData: any
): ColumnType<LegalFormalityIndexTableType> => ({
  filtered: !!variables.filters?.[`${dataIndex}`],
  filteredValue: variables.filters?.[`${dataIndex}`],
  filters: assignedUserEmailsList(legalisteData),
})

export const buildCellLink = (url: string, innerLink: any, record: any) => {
  return (
    <LinkCell>
      <Link to={url}>{innerLink}</Link>
    </LinkCell>
  )
}

const columnSortOrder = (variables: any, columnName: string) =>
  variables.sorter?.field === columnName ? variables.sorter.order : false

const adminColumns = (t: any, variables: any, canFullAccess: boolean) =>
  // canFullAccess ? [] : []
  []

export const columns = (
  t: any,
  variables: any,
  canFullAccess: boolean,
  legalisteData: any
) => [
  {
    title: 'Id',
    dataIndex: 'id',
    key: 'id',
    sorter: true,
    sortOrder: columnSortOrder(variables, 'id'),
  },
  {
    title: 'Type',
    dataIndex: 'productIdentificationToken',
    key: 'productIdentificationToken',
    sorter: true,
    sortOrder: columnSortOrder(variables, 'productIdentificationToken'),
  },
  {
    title: 'Provider',
    dataIndex: 'formProvider',
    key: 'formProvider',
    sorter: true,
    sortOrder: columnSortOrder(variables, 'formProvider'),
  },
  {
    title: t('legalFormalities:columns.status'),
    dataIndex: 'legalFormalityStatus',
    key: 'legalFormalityStatus',
    sorter: true,
    sortOrder: columnSortOrder(variables, 'legalFormalityStatus'),
    ...getFormalitiesStatusColumnSearchProps(variables, 'legalFormalityStatus'),
  },
  {
    title: t('lastname'),
    dataIndex: 'lastname',
    key: 'lastname',
    sorter: true,
    sortOrder: columnSortOrder(variables, 'lastname'),
  },
  {
    title: t('firstname'),
    dataIndex: 'firstname',
    key: 'firstname',
    sorter: true,
    sortOrder: columnSortOrder(variables, 'firstname'),
  },
  {
    title: t('purchases:columns.email'),
    dataIndex: 'email',
    key: 'email',
    sorter: true,
    sortOrder: columnSortOrder(variables, 'email'),
  },
  {
    title: t('purchases:columns.society-name'),
    dataIndex: 'societyName',
    key: 'societyName',
    sorter: true,
    sortOrder: columnSortOrder(variables, 'societyName'),
  },
  {
    title: 'Contact',
    dataIndex: 'contactId',
    key: 'contactId',
    render: (text: string, record: LegalFormalityIndexTableType) =>
      buildCellLink(
        `/contacts/${record.contactId}/legal_formalities/${record.id}`,
        record.contactId,
        record
      ),
  },
  {
    title: t('legalFormalities:show.misc.assigned-to'),
    dataIndex: 'assignedUserEmail',
    key: 'assignedUserEmail',
    ...getAssignedUserColumnSearchProps(
      variables,
      'assignedUserEmail',
      legalisteData
    ),
  },
  {
    title: t('purchases:columns.created-at'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    sorter: true,
    sortOrder: columnSortOrder(variables, 'createdAt'),
  },
  {
    title: t('purchases:columns.status'),
    dataIndex: 'purchaseStatus',
    key: 'purchaseStatus',
    sorter: true,
    sortOrder: columnSortOrder(variables, 'purchaseStatus'),
    ...getPurchaseStatusColumnSearchProps(variables, 'purchaseStatus'),
  },
  ...adminColumns(t, variables, canFullAccess),
]

const LinkCell = styled.div`
  width: 100%;

  & > a {
    color: white;
    font-weight: bold;
    background-color: #0d80ff;
    padding: 10px;
    border-radius: 8px;
  }
`
